import { randomUUID } from 'crypto'
import type { SupabaseClient } from '@supabase/supabase-js'

/**
 * Single-cycle refund for an attendee row.
 *
 * One call inserts AT MOST one REFUND (+1). The refund targets the lot
 * tied to the *newest* USAGE on this attendee — that's the current
 * booking cycle we're cancelling. If that lot is already balanced (net
 * ≥ 0 across all USAGE + REFUND rows keyed to it), nothing happens.
 *
 * Why this shape and not "match every USAGE with a REFUND":
 *
 *   Rebook cycles reuse the same attendee row (@@unique[supervisionId,
 *   proId] on the schema). Two real scenarios break naive helpers:
 *
 *   1. book → cancel (REFUND) → rebook (USAGE) → cancel again
 *      A "any prior REFUND exists → skip" helper sees the old REFUND
 *      and returns 0 — the new credit is silently burned.
 *
 *   2. book → late-cancel (LATE_CANCEL, no REFUND per policy) → rebook
 *      (USAGE) → supervisor removes
 *      A "insert |net| REFUND rows per lot" helper sees net = -2 on the
 *      lot and inserts 2 refunds — over-crediting the forfeited cycle.
 *
 *   Refunding exactly one cycle (the current one) handles both. The
 *   forfeited-cycle USAGE stays unpaired in the ledger, which is the
 *   accurate representation of the LATE_CANCEL policy outcome.
 *
 * signupId is carried forward from the newest USAGE so per-signup
 * ledger reconciliation (schema.prisma supervision_credits.signupId)
 * pairs REFUND ↔ USAGE cleanly.
 *
 * Shared across therapist + between-sessions so both cancel surfaces
 * write the ledger the same way.
 */

/** DB column width for supervision_credits.note (VARCHAR(200)). */
const NOTE_MAX_LENGTH = 200

export interface RefundAttendeeResult {
  refundId: string | null
  refunded: 0 | 1
  alreadyBalanced: boolean
}

interface CreditRow {
  id: string
  kind: 'USAGE' | 'REFUND'
  amount: number
  proId: string
  forType: string
  sourceCreditId: string | null
  signupId: string | null
  createdAt: string
}

export async function refundAttendeeCycle(
  admin: SupabaseClient,
  attendeeId: string,
  note?: string | null,
): Promise<RefundAttendeeResult> {
  const { data: rows } = await admin
    .from('supervision_credits')
    .select('id, kind, amount, "proId", "forType", "sourceCreditId", "signupId", "createdAt"')
    .eq('attendeeId', attendeeId)
    .in('kind', ['USAGE', 'REFUND'])
    .order('createdAt', { ascending: false })

  const ledger = (rows ?? []) as CreditRow[]
  const newestUsage = ledger.find(r => r.kind === 'USAGE')
  if (!newestUsage) {
    // No credit was ever spent on this attendee (Alli intern on
    // INDIVIDUAL/DYAD, one-off freebie, etc.). Nothing to refund.
    return { refundId: null, refunded: 0, alreadyBalanced: true }
  }

  if (!newestUsage.sourceCreditId) {
    // Legacy/imported USAGE rows sometimes carry a null lot. Log so ops
    // can reconcile the ledger, but let the cancel proceed — hard-failing
    // here would make the seat impossible to cancel through the normal
    // UI path. All present-day USAGE writers set sourceCreditId, so any
    // hit here is a data-hygiene signal, not a runtime bug.
    console.warn('[refundAttendeeCycle] USAGE has null sourceCreditId — skipping refund', {
      attendeeId,
      usageId: newestUsage.id,
    })
    return { refundId: null, refunded: 0, alreadyBalanced: true }
  }

  const lotKey: string = newestUsage.sourceCreditId
  const lotNet = ledger
    .filter(r => r.sourceCreditId === lotKey)
    .reduce((sum, r) => sum + (r.amount ?? 0), 0)

  if (lotNet >= 0) {
    // Current cycle already balanced — retry, prior manual refund, or
    // the newest USAGE was itself a re-book that hasn't been cancelled
    // yet. Nothing to do.
    return { refundId: null, refunded: 0, alreadyBalanced: true }
  }

  const refundId = randomUUID()
  // Clamp the note to the DB column width so a long caller-supplied note
  // (e.g. member cancellation reason concatenated with a policy prefix)
  // doesn't 22001 the insert and get logged as a silent refund failure
  // while the API reports refunded: true.
  const clampedNote = note ? note.slice(0, NOTE_MAX_LENGTH) : null
  const { error } = await admin.from('supervision_credits').insert({
    id: refundId,
    proId: newestUsage.proId,
    kind: 'REFUND',
    forType: newestUsage.forType,
    amount: 1,
    sourceCreditId: newestUsage.sourceCreditId,
    signupId: newestUsage.signupId,
    attendeeId,
    note: clampedNote,
    createdAt: new Date().toISOString(),
  })
  if (error) {
    console.error('[refundAttendeeCycle] insert failed', error, { attendeeId })
    return { refundId: null, refunded: 0, alreadyBalanced: false }
  }
  return { refundId, refunded: 1, alreadyBalanced: false }
}
